import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Star,
  MessageCircle,
  Eye,
  EyeOff,
  CheckCircle,
  Clock,
  Image,
  Video,
  Send,
  Loader2,
  Filter,
  Trash2,
  ChevronRight,
  X,
  Play
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { EmptyStatePresets } from "../components/EmptyState";

const STATUS_CONFIG = {
  pending: { label: "En attente", color: "bg-yellow-500", icon: Clock },
  approved: { label: "Approuvé", color: "bg-green-500", icon: CheckCircle },
  hidden: { label: "Masqué", color: "bg-gray-500", icon: EyeOff }
};

export default function Reviews() {
  const { getAuthHeaders } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Detail dialog
  const [selectedReview, setSelectedReview] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  
  // Response dialog
  const [responseOpen, setResponseOpen] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [sendingResponse, setSendingResponse] = useState(false);
  
  // Media viewer
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [mediaViewerContent, setMediaViewerContent] = useState(null);
  const [mediaViewerType, setMediaViewerType] = useState("image");

  const fetchReviews = async () => {
    try {
      const [reviewsRes, statsRes] = await Promise.all([
        axios.get(`${API}/reviews${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`, getAuthHeaders()),
        axios.get(`${API}/reviews/stats`, getAuthHeaders())
      ]);
      setReviews(reviewsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des avis");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [statusFilter]);

  const openReviewDetail = (review) => {
    setSelectedReview(review);
    setResponseText(review.admin_response || "");
    setDetailOpen(true);
  };

  const updateStatus = async (reviewId, newStatus) => {
    try {
      await axios.put(
        `${API}/reviews/${reviewId}/status`,
        { status: newStatus },
        getAuthHeaders()
      );
      toast.success(newStatus === 'approved' ? "Avis approuvé" : "Avis masqué");
      fetchReviews();
      if (selectedReview?.id === reviewId) {
        setSelectedReview(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const sendResponse = async () => {
    if (!responseText.trim() || !selectedReview) return;
    
    setSendingResponse(true);
    try {
      await axios.post(
        `${API}/reviews/${selectedReview.id}/respond`,
        { content: responseText },
        getAuthHeaders()
      );
      toast.success("Réponse ajoutée");
      setSelectedReview(prev => ({ ...prev, admin_response: responseText }));
      setResponseOpen(false);
      fetchReviews();
    } catch (error) {
      toast.error("Erreur lors de l'envoi de la réponse");
    } finally {
      setSendingResponse(false);
    }
  };

  const deleteResponse = async () => {
    if (!selectedReview) return;
    
    try {
      await axios.delete(
        `${API}/reviews/${selectedReview.id}/respond`,
        getAuthHeaders()
      );
      toast.success("Réponse supprimée");
      setSelectedReview(prev => ({ ...prev, admin_response: null }));
      setResponseText("");
      fetchReviews();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const openMediaViewer = (content, type) => {
    setMediaViewerContent(content);
    setMediaViewerType(type);
    setMediaViewerOpen(true);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric'
    });
  };

  const renderStars = (rating, size = "w-4 h-4") => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star 
            key={star}
            className={`${size} ${star <= rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reviews-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Avis Clients</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gérez les avis et répondez à vos clientes
          </p>
        </div>
        {stats?.average_rating > 0 && (
          <div className="flex items-center gap-2 bg-secondary rounded-lg px-4 py-2">
            <Star className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" />
            <span className="text-lg font-bold">{stats.average_rating}</span>
            <span className="text-sm text-muted-foreground">/ 5</span>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          {Object.entries(STATUS_CONFIG).map(([key, config]) => {
            const count = stats[key] || 0;
            const Icon = config.icon;
            return (
              <Card 
                key={key}
                className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === key ? 'ring-2 ring-primary' : ''}`}
                onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
              >
                <CardContent className="p-4 text-center">
                  <div className={`w-8 h-8 rounded-full ${config.color} mx-auto mb-2 flex items-center justify-center`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-xs text-muted-foreground">{config.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Rating Distribution */}
      {stats?.rating_distribution && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm font-medium">Distribution :</span>
              {[5, 4, 3, 2, 1].map((rating) => (
                <div key={rating} className="flex items-center gap-1">
                  <span className="text-sm font-medium">{rating}</span>
                  <Star className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                  <span className="text-sm text-muted-foreground">
                    ({stats.rating_distribution[rating] || 0})
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        statusFilter !== 'all' ? (
          <EmptyStatePresets.NoSearchResults searchTerm={`statut: ${STATUS_CONFIG[statusFilter]?.label || statusFilter}`} />
        ) : (
          <EmptyStatePresets.NoReviews />
        )
      ) : (
        <div className="space-y-3">
          {reviews.map(review => {
            const statusConfig = STATUS_CONFIG[review.status];
            const StatusIcon = statusConfig?.icon || Clock;
            const hasMedia = review.photos?.length > 0 || review.video;
            
            return (
              <Card 
                key={review.id}
                className={`cursor-pointer hover:shadow-md transition-all ${review.status === 'pending' ? 'border-yellow-500 border-2' : ''}`}
                onClick={() => openReviewDetail(review)}
                data-testid={`review-${review.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Rating */}
                    <div className="shrink-0">
                      {renderStars(review.rating)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{review.customer_name}</span>
                        <Badge variant="outline" className={`text-xs ${statusConfig?.color} text-white border-0`}>
                          {statusConfig?.label}
                        </Badge>
                        {hasMedia && (
                          <div className="flex gap-1">
                            {review.photos?.length > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                <Image className="w-3 h-3 mr-1" />
                                {review.photos.length}
                              </Badge>
                            )}
                            {review.video && (
                              <Badge variant="secondary" className="text-xs">
                                <Video className="w-3 h-3" />
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {review.comment && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                          {review.comment}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{review.order_number}</span>
                        <span>•</span>
                        <span>{formatDate(review.created_at)}</span>
                        {review.admin_response && (
                          <>
                            <span>•</span>
                            <span className="text-green-600 flex items-center gap-1">
                              <MessageCircle className="w-3 h-3" />
                              Répondu
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Review Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="review-detail-dialog">
          {selectedReview && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="flex items-center gap-2">
                      {renderStars(selectedReview.rating, "w-5 h-5")}
                    </DialogTitle>
                    <DialogDescription>
                      {selectedReview.customer_name} • {formatDate(selectedReview.created_at)}
                    </DialogDescription>
                  </div>
                  <Badge className={`${STATUS_CONFIG[selectedReview.status]?.color} text-white`}>
                    {STATUS_CONFIG[selectedReview.status]?.label}
                  </Badge>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Order Info */}
                <div className="bg-secondary rounded-lg p-3">
                  <p className="text-sm text-muted-foreground mb-1">Commande {selectedReview.order_number}</p>
                  {selectedReview.order_items?.map((item, idx) => (
                    <p key={idx} className="text-sm font-medium">
                      {item.product_name} - {item.size} / {item.color}
                    </p>
                  ))}
                </div>

                {/* Comment */}
                {selectedReview.comment && (
                  <div>
                    <Label className="text-muted-foreground">Commentaire</Label>
                    <p className="mt-1 text-foreground whitespace-pre-wrap">{selectedReview.comment}</p>
                  </div>
                )}

                {/* Photos */}
                {selectedReview.photos?.length > 0 && (
                  <div>
                    <Label className="text-muted-foreground mb-2 block">Photos ({selectedReview.photos.length})</Label>
                    <div className="flex gap-2 flex-wrap">
                      {selectedReview.photos.map((photo, idx) => (
                        <button
                          key={idx}
                          onClick={() => openMediaViewer(photo, 'image')}
                          className="w-24 h-24 rounded-lg overflow-hidden hover:opacity-80 transition-opacity"
                        >
                          <img src={photo} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Video */}
                {selectedReview.video && (
                  <div>
                    <Label className="text-muted-foreground mb-2 block">Vidéo</Label>
                    <button
                      onClick={() => openMediaViewer(selectedReview.video, 'video')}
                      className="relative w-40 h-24 rounded-lg overflow-hidden bg-black group"
                    >
                      <video src={selectedReview.video} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/60 transition-colors">
                        <Play className="w-8 h-8 text-white" />
                      </div>
                    </button>
                  </div>
                )}

                {/* Admin Response */}
                {selectedReview.admin_response && (
                  <div className="bg-primary/10 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-primary font-medium">Votre réponse</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={deleteResponse}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{selectedReview.admin_response}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  {selectedReview.status === 'pending' && (
                    <Button 
                      onClick={() => updateStatus(selectedReview.id, 'approved')}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approuver
                    </Button>
                  )}
                  
                  {selectedReview.status !== 'hidden' && (
                    <Button 
                      variant="outline"
                      onClick={() => updateStatus(selectedReview.id, 'hidden')}
                    >
                      <EyeOff className="w-4 h-4 mr-2" />
                      Masquer
                    </Button>
                  )}
                  
                  {selectedReview.status === 'hidden' && (
                    <Button 
                      onClick={() => updateStatus(selectedReview.id, 'approved')}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Rendre visible
                    </Button>
                  )}
                  
                  {!selectedReview.admin_response && (
                    <Button 
                      variant="outline"
                      onClick={() => setResponseOpen(true)}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Répondre
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Response Dialog */}
      <Dialog open={responseOpen} onOpenChange={setResponseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Répondre à l'avis</DialogTitle>
            <DialogDescription>
              Votre réponse sera visible publiquement sous l'avis de {selectedReview?.customer_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Textarea
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              placeholder="Merci pour votre retour..."
              rows={4}
              data-testid="review-response-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setResponseOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={sendResponse}
              disabled={!responseText.trim() || sendingResponse}
            >
              {sendingResponse ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Publier la réponse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Viewer Dialog */}
      <Dialog open={mediaViewerOpen} onOpenChange={setMediaViewerOpen}>
        <DialogContent className="max-w-4xl p-0 bg-black border-0">
          <button
            onClick={() => setMediaViewerOpen(false)}
            className="absolute top-4 right-4 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
          >
            <X className="w-5 h-5" />
          </button>
          {mediaViewerType === 'image' ? (
            <img 
              src={mediaViewerContent} 
              alt="Photo" 
              className="w-full h-auto max-h-[90vh] object-contain"
            />
          ) : (
            <video 
              src={mediaViewerContent} 
              controls 
              autoPlay
              className="w-full h-auto max-h-[90vh]"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
